import { Link } from 'react-router-dom';
import AddIcon from '@mui/icons-material/Add';
import ContentCopyOutlinedIcon from '@mui/icons-material/ContentCopyOutlined';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import { AssignmentEntityType, getMaxPercentLabel, getMinPercentLabel } from 'domain/shared/assignment';
import { EmployeeLink } from 'domain/shared/employee';
import BudgetLink from 'domain/shared/budget/field/BudgetLink';
import { PROJECT_MEMBERSHIP_ROLES } from 'domain/shared/core/constants';
import { Badge } from 'framework/ui/mui-builder';
import { ShowDate, type FlatListTableProps, type ListTableColumn, type ListTableAction } from 'framework/ui/ui-builder';
import { EMPTY_VALUE_INDICATOR } from 'framework/utils';
import { DATE_FORMAT } from 'framework/utils/hooks/useDateService';
import AssignmentTargetLink from './AssignmentTargetLink';
import { getAssigneeName, isIndividualAssignment } from '../shared/assignmentDisplay';

type Assignment = AssignmentEntityType;
type PercentKey = 'minPercent' | 'maxPercent';
const membershipRoleLabels = new Map<string, string>(Object.entries(PROJECT_MEMBERSHIP_ROLES));

interface AssignmentTableActions {
  canCreate: boolean;
  canDelete: boolean;
  userAlias?: string | null;
  onCreate: () => void;
  onCopy: (row: Assignment) => void;
  onEdit: (row: Assignment) => void;
  onDelete: (row: Assignment) => void;
}

const sumPercent = (rows: Assignment[], key: PercentKey) => rows.reduce((total, row) => total + (+row[key] || 0), 0);

const renderPercent = (value: Nullable<number> | undefined) => (value != null ? `${value}%` : EMPTY_VALUE_INDICATOR);

const renderDate = (value: Nullable<string> | undefined) =>
  value ? <ShowDate dateUTC={value} format={DATE_FORMAT.SIMPLE} isDateTime={false} /> : EMPTY_VALUE_INDICATOR;

const renderAssignee = (row: Assignment) => {
  if (!isIndividualAssignment(row)) {
    return getAssigneeName(row) || EMPTY_VALUE_INDICATOR;
  }
  return row.employeeId ? <EmployeeLink id={row.employeeId} name={row.employeeName} /> : EMPTY_VALUE_INDICATOR;
};

const canDeleteAssignment = (row: Assignment, { canDelete, userAlias }: AssignmentTableActions) =>
  canDelete || Boolean(userAlias && row.createdById === userAlias);

const getColumns = (
  rows: Assignment[],
  getDetailsUrl: (id: Assignment['id']) => string,
): ListTableColumn<Assignment>[] => [
  {
    id: 'id',
    label: 'ID',
    width: 100,
    sortable: true,
    render: row => (
      <>
        <Link to={getDetailsUrl(row.id)}>{row.id}</Link>
        {row.bench && (
          <Badge color="primary" sx={{ ml: 1 }}>
            Bench
          </Badge>
        )}
      </>
    ),
  },
  {
    id: 'target.id',
    label: 'Target',
    width: 130,
    render: row => <AssignmentTargetLink target={row.target} />,
  },
  {
    id: 'ownerNameEn',
    label: 'Owner',
    width: 130,
    sortable: true,
    render: row => (row.ownerId ? <EmployeeLink id={row.ownerId} name={row.ownerName} /> : EMPTY_VALUE_INDICATOR),
  },
  {
    id: 'assignee',
    label: 'Assignee',
    render: renderAssignee,
  },
  {
    id: 'position',
    label: 'Worker position',
    render: row => (isIndividualAssignment(row) ? row.position?.name || EMPTY_VALUE_INDICATOR : 'Public Service'),
  },
  {
    id: 'membershipRole',
    label: 'Membership role',
    render: row => membershipRoleLabels.get(row.membershipRole ?? '') || EMPTY_VALUE_INDICATOR,
  },
  {
    id: 'backupForEmployeeId',
    label: 'Backup for',
    width: 150,
    sortable: true,
    render: row =>
      row.backupForEmployeeId ? (
        <EmployeeLink id={row.backupForEmployeeId} name={row.backupForEmployeeName} />
      ) : (
        EMPTY_VALUE_INDICATOR
      ),
  },
  {
    id: 'from',
    label: 'Start date',
    width: 140,
    sortable: true,
    render: row => renderDate(row.from),
  },
  {
    id: 'to',
    label: 'End date',
    width: 140,
    sortable: true,
    render: row => renderDate(row.to),
  },
  {
    id: 'budget.id',
    label: 'Budget',
    width: 140,
    render: row => (row.budget?.id ? <BudgetLink id={row.budget.id} name={row.budget.name} /> : EMPTY_VALUE_INDICATOR),
  },
  {
    id: 'minPercent',
    label: getMinPercentLabel(sumPercent(rows, 'minPercent')),
    width: 150,
    sortable: true,
    render: row => renderPercent(row.minPercent),
  },
  {
    id: 'maxPercent',
    label: getMaxPercentLabel(sumPercent(rows, 'maxPercent')),
    width: 150,
    sortable: true,
    render: row => renderPercent(row.maxPercent),
  },
];

const getRowActions = (actions: AssignmentTableActions): ListTableAction<Assignment>[] => [
  {
    id: 'copy',
    label: 'Copy assignment',
    tooltip: 'Copy assignment',
    icon: <ContentCopyOutlinedIcon />,
    variant: 'outlined',
    isVisible: () => actions.canCreate,
    onClick: actions.onCopy,
  },
  {
    id: 'edit',
    label: 'Edit assignment',
    tooltip: 'Edit assignment',
    icon: <EditOutlinedIcon />,
    variant: 'outlined',
    isVisible: row => row.canEdit,
    onClick: actions.onEdit,
  },
  {
    id: 'delete',
    label: 'Delete assignment',
    tooltip: 'Delete assignment',
    icon: <DeleteOutlineIcon />,
    variant: 'outlined',
    color: 'error',
    isVisible: row => canDeleteAssignment(row, actions),
    onClick: actions.onDelete,
  },
];

export const getAssignmentsTableConfig = (
  rows: Assignment[],
  getDetailsUrl: (id: Assignment['id']) => string,
  actions: AssignmentTableActions,
): FlatListTableProps<Assignment> => ({
  rows,
  columns: getColumns(rows, getDetailsUrl),
  rowActions: { actions: getRowActions(actions) },
  mainAction: actions.canCreate
    ? {
        id: 'create',
        label: 'Create assignment',
        tooltip: 'Create assignment',
        icon: <AddIcon />,
        variant: 'contained',
        onClick: actions.onCreate,
      }
    : undefined,
  emptyContent: 'No assignments found.',
});
